import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Flag, Plus, Trash, Trophy, Moon, Heartbeat, Lightning } from "@phosphor-icons/react";

const METRICS = [
  { id: "sleep_h", label: "Сон", unit: "ч", icon: <Moon size={14} className="text-[#a970ff]" />, def: 7 },
  { id: "recovery", label: "Восстановление", unit: "%", icon: <Heartbeat size={14} className="text-[#00e676]" />, def: 60 },
  { id: "strain", label: "Нагрузка", unit: "", icon: <Lightning size={14} className="text-[#0084ff]" />, def: 10 },
];
const dotColor = { pass: "bg-[#00e676]", fail: "bg-[#ff3b30]", nodata: "bg-white/15" };

function CreateDialog({ open, onClose, onCreated }) {
  const [metric, setMetric] = useState("sleep_h");
  const [target, setTarget] = useState(7);
  const [daysNeeded, setDaysNeeded] = useState(7);
  const m = METRICS.find((x) => x.id === metric);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/family/challenges", { metric, target: Number(target), days_needed: Number(daysNeeded) });
      toast.success("Челлендж запущен — вперёд, семья!");
      onCreated();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass-modal">
        <DialogHeader><DialogTitle>Новый семейный челлендж</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-2">
            {METRICS.map((x) => (
              <button key={x.id} type="button" onClick={() => { setMetric(x.id); setTarget(x.def); }} data-testid={`ch-metric-${x.id}`}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-semibold transition-colors ${metric === x.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {x.icon} {x.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Цель (≥{m.unit ? `, ${m.unit}` : ""})</label>
              <Input type="number" step="0.5" min="1" value={target} onChange={(e) => setTarget(e.target.value)} required data-testid="ch-target-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Дней подряд</label>
              <Input type="number" min="1" max="90" value={daysNeeded} onChange={(e) => setDaysNeeded(e.target.value)} required data-testid="ch-days-input" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">День засчитывается, когда каждый участник с Whoop достигает цели.</p>
          <Button type="submit" className="w-full" data-testid="ch-create-submit">Запустить челлендж</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function FamilyChallenges() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    api.get("/family/challenges").then((r) => setData(r.data)).catch(() => setData({ active: [], done: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/family/challenges/${id}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="glass-card p-5" data-testid="family-challenges">
      <div className="flex items-center gap-2 mb-3">
        <Flag size={18} weight="duotone" className="text-[#ff9100]" />
        <h3 className="text-sm font-semibold flex-1">Семейные челленджи</h3>
        <button onClick={() => setShowCreate(true)} data-testid="ch-new-btn"
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-secondary text-muted-foreground hover:text-foreground transition-colors">
          <Plus size={13} /> Новый
        </button>
      </div>
      {!data ? (
        <div className="h-16 animate-pulse rounded-lg bg-white/[0.04]" />
      ) : data.active.length === 0 && data.done.length === 0 ? (
        <p className="text-xs text-muted-foreground">Запустите первый челлендж — например, «Сон ≥ 7 ч у всей семьи — 7 дней подряд».</p>
      ) : (
        <div className="space-y-4">
          {data.active.map((ch) => (
            <div key={ch.id} data-testid={`challenge-${ch.id}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold flex-1 truncate">{ch.title}</span>
                <span className="text-xs num font-bold text-primary" data-testid={`ch-streak-${ch.id}`}>{ch.streak}/{ch.days_needed} дн.</span>
                {(ch.created_by === user?.id) && (
                  <button onClick={() => remove(ch.id)} data-testid={`ch-delete-${ch.id}`}
                    className="p-1 text-muted-foreground hover:text-red-400 transition-colors"><Trash size={14} /></button>
                )}
              </div>
              <div className="h-1.5 rounded-full bg-white/[0.06] mt-1.5 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-primary to-[#00e676] transition-all"
                  style={{ width: `${Math.min(100, (ch.streak / ch.days_needed) * 100)}%` }} />
              </div>
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {ch.days.map((d) => (
                  <span key={d.date} title={d.date} className={`w-2 h-2 rounded-full ${dotColor[d.status]}`} />
                ))}
                <span className="text-[10px] text-muted-foreground ml-1">
                  {ch.participants > 0 ? `участвуют ${ch.participants} из ${ch.members_total}` : "никто не подключил Whoop"}
                </span>
              </div>
            </div>
          ))}
          {data.done.length > 0 && (
            <div className="pt-2 border-t border-white/[0.06] space-y-1.5" data-testid="ch-done-list">
              {data.done.map((ch) => (
                <div key={ch.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Trophy size={13} weight="fill" className="text-[#ffd700] shrink-0" />
                  <span className="flex-1 truncate">{ch.title}</span>
                  {ch.completed_at && <span className="num">{new Date(ch.completed_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <CreateDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
    </div>
  );
}
